import { Box, Tab, Tabs } from '@mui/material';
import type { FC, SyntheticEvent } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { ADMIN_ROLES, LICENSE_MENU_ROLES } from 'data/constants/roles';
import useTranslate from 'hooks/useTranslate';
import type { RootState } from 'redux/store';

type AccountTab = 'account' | 'password' | 'licenses' | 'signatures';

interface AccountTabItem {
    label: string;
    path: string;
    value: AccountTab;
    visible: boolean;
}

const ACCOUNT_TAB_PATHS: Record<AccountTab, string> = {
    account: '/account/my-account',
    password: '/account/my-account#change-password',
    licenses: '/account/licenses',
    signatures: '/account/signatures',
};

const getActiveTab = (pathname: string, hash: string): AccountTab => {
    if (pathname.startsWith(ACCOUNT_TAB_PATHS.licenses)) return 'licenses';
    if (pathname === '/signatures' || pathname.startsWith(ACCOUNT_TAB_PATHS.signatures)) {
        return 'signatures';
    }
    if (hash === '#change-password') return 'password';
    return 'account';
};

const AccountTabs: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { translate } = useTranslate();
    const role = useSelector((state: RootState) => state.user.entity.role);
    const activeTab = getActiveTab(location.pathname, location.hash);

    const tabs: AccountTabItem[] = [
        {
            label: 'account',
            path: ACCOUNT_TAB_PATHS.account,
            value: 'account',
            visible: true,
        },
        {
            label: 'changePassword',
            path: ACCOUNT_TAB_PATHS.password,
            value: 'password',
            visible: true,
        },
        {
            label: 'licenses',
            path: ACCOUNT_TAB_PATHS.licenses,
            value: 'licenses',
            visible: LICENSE_MENU_ROLES.includes(role),
        },
        {
            label: 'signatures',
            path: ACCOUNT_TAB_PATHS.signatures,
            value: 'signatures',
            visible:
                activeTab === 'signatures' ||
                ADMIN_ROLES.some((allowedRole) => allowedRole === role),
        },
    ];
    const visibleTabs = tabs.filter((tab) => tab.visible);
    const selectedTab = visibleTabs.some((tab) => tab.value === activeTab) ? activeTab : false;

    const handleChange = (_event: SyntheticEvent, value: AccountTab) => {
        const destination = visibleTabs.find((tab) => tab.value === value);
        if (destination) navigate(destination.path);
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Tabs
                value={selectedTab}
                onChange={handleChange}
                variant="fullWidth"
                aria-label={translate('account')}
                sx={{
                    minHeight: 44,
                    '& .MuiTabs-list': {
                        flexWrap: 'wrap',
                    },
                    '& .MuiTabs-indicator': {
                        display: 'none',
                    },
                    '& .MuiTab-root': {
                        flex: '1 1 auto',
                        minWidth: 'max-content',
                        maxWidth: 'none',
                        minHeight: 44,
                        px: { xs: 1.25, sm: 2 },
                        fontWeight: 600,
                        lineHeight: 1.2,
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        borderBottom: 2,
                        borderColor: 'divider',
                        '&.Mui-selected': {
                            borderColor: 'primary.main',
                        },
                    },
                }}
            >
                {visibleTabs.map((tab) => (
                    <Tab key={tab.value} label={translate(tab.label)} value={tab.value} />
                ))}
            </Tabs>
        </Box>
    );
};

export default AccountTabs;
